import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowRight, ChevronRight, Wrench } from 'lucide-react'
import { equipmentApi } from '@/api/equipmentApi'
import { badgeVariants } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { UnitMaintenanceTaskDialog } from '@/components/equipment/storage/UnitMaintenanceTaskDialog'
import { useEquipmentTypes } from '@/hooks/useEquipment'
import { useEventTypes } from '@/hooks/useEventTypes'
import { useTenant } from '@/providers/TenantProvider'
import {
  formatDueDay,
  groupUnitMaintenance,
  humanizeEventType,
  type UnitMaintenanceTask,
} from '@/lib/unitMaintenance'
import { cn } from '@/lib/utils'

const NEUTRAL_DOT = '#64748b'

/**
 * Outstanding maintenance for the storage unit this page is showing.
 *
 * The storage unit surface showed layout and capacity only, so nothing told the
 * person looking at FRZ-01 that it has a PM due Thursday — which is precisely
 * the fact that decides whether they should be putting anything into it.
 *
 * One row per TASK, not per occurrence (z8nrz7f2fa): generation writes a row per
 * due date, so a daily cleaning listed itself dozens of times and buried the
 * page. Each task row summarises cadence, how far behind it is and when it is
 * next due; clicking it opens the occurrences in a dialog, so the schedule
 * stays reachable without growing the page.
 *
 * Reads the shared `['maintenance-events', …]` feed and filters client-side
 * rather than adding an `equipmentId` filter to the endpoint. The feed has no
 * such filter today, and this query key is the one the planner already uses, so
 * the panel is usually served from cache. If the feed grows past a tenant's
 * open work being cheap to hold, add the server-side filter then — the swap is
 * local to this file.
 *
 * Renders nothing while loading or when nothing is scheduled or in progress: a
 * full-width card that only says "No maintenance scheduled" pushes the map
 * down for no information.
 */
export function UnitMaintenancePanel({ equipmentId }: { equipmentId: string }) {
  const { selectedTenantId } = useTenant()

  const events = useQuery({
    queryKey: ['maintenance-events', selectedTenantId, {}] as const,
    enabled: selectedTenantId !== null,
    queryFn: () => equipmentApi.listEvents(selectedTenantId as string),
  })
  // Only for the cadence label and the task's display name; the rows render
  // without them and fill in when they land.
  const types = useEquipmentTypes()
  const { eventTypes } = useEventTypes()

  const tasks = useMemo(() => {
    const schedules = new Map((types.data ?? []).map((t) => [t.id, t.schedules]))
    return groupUnitMaintenance(events.data ?? [], equipmentId, (id) => schedules.get(id) ?? [])
  }, [events.data, equipmentId, types.data])

  const eventTypeMeta = useMemo(
    () => new Map(eventTypes.map((t) => [t.code, { label: t.label, color: t.color }])),
    [eventTypes],
  )
  const display = (task: UnitMaintenanceTask) => {
    const meta = eventTypeMeta.get(task.eventTypeCode)
    return {
      taskName: meta?.label ?? humanizeEventType(task.eventTypeCode),
      color: meta?.color ?? NEUTRAL_DOT,
      cadence: task.recurrence ?? (task.oneOff ? 'One-off' : null),
    }
  }

  // By key, not the object: a refetch rebuilds the tasks, and the open dialog
  // should show the fresh one rather than a stale copy.
  const [openKey, setOpenKey] = useState<string | null>(null)
  const openTask = tasks.find((t) => t.key === openKey) ?? null

  const behind = tasks.filter((t) => t.overdueCount > 0).length

  if (events.isLoading || (!events.isError && tasks.length === 0)) return null

  return (
    <Card>
      <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-2'>
        <CardTitle className='flex items-center gap-2 text-sm font-medium'>
          <Wrench className='h-4 w-4 text-muted-foreground' aria-hidden />
          Maintenance
          {/* Past the early return, a non-error card always has tasks. */}
          {!events.isError && (
            <span className='font-normal text-muted-foreground'>
              · {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
              {behind > 0 && <span className='text-rose-700'>, {behind} behind</span>}
            </span>
          )}
        </CardTitle>
        <Link
          to='/inventory?tab=tasks'
          className='inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline'
        >
          Open in Tasks
          <ArrowRight className='h-3 w-3' aria-hidden />
        </Link>
      </CardHeader>
      <CardContent>
        {events.isError ? (
          <div className='flex flex-wrap items-center gap-3 py-2 text-sm'>
            <span className='text-rose-700'>Couldn't load maintenance for this unit.</span>
            <Button variant='outline' size='sm' onClick={() => events.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <ul className='divide-y rounded-md border'>
            {tasks.map((task) => (
              <TaskRow key={task.key} task={task} {...display(task)} onOpen={() => setOpenKey(task.key)} />
            ))}
          </ul>
        )}
      </CardContent>
      <UnitMaintenanceTaskDialog
        task={openTask}
        {...(openTask ? display(openTask) : { taskName: '', color: NEUTRAL_DOT, cadence: null })}
        onClose={() => setOpenKey(null)}
      />
    </Card>
  )
}

function TaskRow({
  task,
  taskName,
  color,
  cadence,
  onOpen,
}: {
  task: UnitMaintenanceTask
  taskName: string
  color: string
  cadence: string | null
  onOpen: () => void
}) {
  return (
    <li>
      <button
        type='button'
        onClick={onOpen}
        aria-haspopup='dialog'
        className='flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-left text-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      >
        <span className='block min-w-0 flex-1'>
          <span className='flex flex-wrap items-center gap-x-1.5'>
            <span className='h-2 w-2 shrink-0 rounded-full' style={{ backgroundColor: color }} aria-hidden />
            <span className='font-medium'>{task.equipmentTypeName}</span>
            <span className='text-muted-foreground'>·</span>
            <span>{taskName}</span>
          </span>
          <span className='mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground'>
            {cadence && (
              <>
                <span>{cadence}</span>
                <span aria-hidden>·</span>
              </>
            )}
            {task.overdueCount > 0 ? (
              <span className='font-medium text-rose-700'>
                {task.overdueCount} overdue · oldest {formatDueDay(task.oldestOverdue as string)}
              </span>
            ) : (
              <span>Up to date</span>
            )}
          </span>
        </span>
        <span className='flex shrink-0 items-center gap-2 tabular-nums'>
          <span className='text-muted-foreground'>
            {task.nextDue ? `Next due ${formatDueDay(task.nextDue)}` : 'Nothing upcoming'}
          </span>
          {task.inProgressCount > 0 && (
            <span className={cn(badgeVariants({ variant: 'outline' }), 'border-amber-300 text-amber-800')}>
              In progress
            </span>
          )}
          {/* One badge per task, from the server's flag (tenant timezone +
              grace) — the same one the worklist reads (z8nrz7dyc7). */}
          {task.overdueCount > 0 && (
            <span className={cn(badgeVariants({ variant: 'outline' }), 'border-rose-300 text-rose-700')}>
              Overdue
            </span>
          )}
          <ChevronRight className='h-4 w-4 text-muted-foreground' aria-hidden />
        </span>
      </button>
    </li>
  )
}
